import { ComponentType, forwardRef, HTMLAttributes, useEffect, useMemo } from 'react';

import { Autocomplete, Box, IconButton, MenuItem, SxProps, TextField, Theme } from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';

import { useLanguage } from '../../hooks';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getAllTaskForms } from '../../store/modules/taskForms.slice';
import { theme } from '../../style/theme';
import { TaskForm } from '../../types';
import { ChevronDownIcon } from '../icons';
import { MetadataList } from '../NewWebForms/Questions/singleChoiceMetadataQuestion/metadataList';

interface SingleFormSelectorMenuProps {
  selectedFormId: number;
  handleSelectFormId: (value: number) => void;
  placeholderText: string;
  displaySelectAllForms?: boolean;
}

const customStyles: Record<string, SxProps<Theme>> = {
  container: {
    width: '100%',
  },
  selectContainer: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    '& .MuiAutocomplete-fullWidth': {
      maxWidth: '547px',
    },
  },
  selectPlaceholder: {
    color: theme.palette.text.disabled,
  },
  selectedValue: {
    whiteSpace: 'nowrap',
  },
  customSelect: {
    '& .MuiInputBase-root': {
      borderRadius: '8px',
      padding: '0px',
      paddingLeft: '6px',
      backgroundColor: 'white',
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          border: (theme: Theme) => `1px solid ${theme.palette.primary.main}`,
        },
      },
      '&:hover': {
        '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
          borderColor: (theme: Theme) => theme.palette.primary.main,
        },
      },
      '& .MuiAutocomplete-endAdornment': {
        top: 'unset',
        '& .MuiIconButton-label': {
          color: (theme: Theme) => theme.palette.secondary.main,
          '& .MuiSvgIcon-root': {
            fontSize: '14px',
          },
        },
      },
    },
    '& .MuiSelect-select.MuiSelect-select': {
      boxSizing: 'border-box',
      padding: '5px 12px',
      height: 'auto',
      borderRadius: '8px',
      lineHeight: (theme: Theme) => theme.typography.body1.lineHeight,
    },
  },
};

export function SingleFormSelectMenu({
  selectedFormId,
  handleSelectFormId,
  placeholderText,
  displaySelectAllForms = false,
}: SingleFormSelectorMenuProps) {
  const dispatch = useAppDispatch();
  const language = useLanguage();

  const { taskForms: forms } = useAppSelector((state) => state.taskForms);

  useEffect(() => {
    dispatch(getAllTaskForms());
  }, []);

  const formOptions = useMemo(() => {
    if (displaySelectAllForms) {
      return [
        {
          id: 0, // The 'ALL FORMS' option is id=0. This is an old req from backend
          name: language.webHooks.new.select_all_forms,
        } as TaskForm,
        ...forms,
      ];
    }
    return forms;
  }, [forms, displaySelectAllForms]);

  const selectedForm = useMemo(() => {
    if (!formOptions || selectedFormId === null) return null;
    return formOptions.find(({ id }) => id === selectedFormId);
  }, [formOptions, selectedFormId]);

  // Virtualization elements for the Form Selection
  const ListboxComponent = forwardRef<HTMLDivElement>(function ListboxComponent(props, ref) {
    return (
      <div ref={ref}>
        <MetadataList {...props} />
      </div>
    );
  });

  if (!forms) return <></>;

  return (
    <Box sx={customStyles.container}>
      <Box sx={customStyles.selectContainer}>
        <Autocomplete
          fullWidth
          sx={customStyles.customSelect}
          disableClearable={true}
          options={formOptions}
          value={selectedForm}
          getOptionLabel={(form) => form.name}
          ListboxComponent={ListboxComponent as ComponentType<HTMLAttributes<HTMLElement>>}
          onChange={(_, value) => handleSelectFormId((value as TaskForm).id)}
          renderOption={(HTMLProps, { name }) => (
            <MenuItem
              {...HTMLProps}
              key={`option-${name}`}
            >
              <Box
                sx={customStyles.selectedValue}
                component='span'
              >
                {name}
              </Box>
            </MenuItem>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant='outlined'
              label=''
              placeholder={placeholderText}
            />
          )}
          popupIcon={<ChevronDownIcon />}
        />
        {selectedForm !== null && (
          <IconButton
            onClick={() => handleSelectFormId(null)}
            size='small'
          >
            <ClearIcon fontSize='small' />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
